import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { trackWait } from '../../lib/waits.ts'
import { api, json } from '../client.ts'
import { useDraftKey } from '../draftKey.ts'
import { qk } from '../keys.ts'
import type {
	Activity,
	ActivityContext,
	AnyAnswer,
	Artifact,
	Attempt,
	Exercise,
	Parts,
	SourceViewMode,
} from '../types.ts'
import type { ContextOptions } from './artifacts.ts'

/** `artifact` is nullable: a source can be deleted while an activity still points at it. */
export type ActivityDetail = {
	activity: Activity
	artifact: Artifact | null
	exercises: Exercise[]
}

export const useActivities = ({ enabled = true }: { enabled?: boolean } = {}) =>
	useQuery({
		queryKey: qk.activities,
		enabled,
		queryFn: async () =>
			(await json<{ activities: Activity[] }>(await api.activities.$get())).activities,
	})

const activityQuery = (id: string) => ({
	queryKey: qk.activity(id),
	// A 404 here is usually someone else's editor link; the page answers it with the learner
	// link and reports only if there is none.
	queryFn: async () =>
		json<ActivityDetail>(await api.activities[':id'].$get({ param: { id } }), [404]),
})

export const useActivity = (id: string | undefined) =>
	useQuery({ ...activityQuery(id ?? ''), enabled: !!id })

/** The activity as the server has it now, whatever the cache says; the cache takes the answer too. */
export const refreshActivity = (qc: QueryClient, id: string) =>
	qc.fetchQuery({ ...activityQuery(id), staleTime: 0 })

/** The learner link for a published activity, by its id. null when there is none. */
export const useLearnerLink = (id: string | undefined, enabled: boolean) =>
	useQuery({
		queryKey: ['learner-link', id],
		enabled: enabled && !!id,
		retry: false,
		queryFn: async () => {
			const res = await api.l.for[':id'].$get({ param: { id: id as string } })
			if (res.status === 404) return null
			return (await json<{ slug: string }>(res)).slug
		},
	})

/** The AI draft. Slow by nature; the caller shows the wait, not a spinner in a corner. */
export const useDraftActivity = (artifactId: string) => {
	const qc = useQueryClient()
	const key = useDraftKey()
	return useMutation({
		mutationFn: (input: {
			context: ActivityContext
			folderId: string | null
			/** What the form was filled with, so the api can say which fields she kept. */
			suggested?: ActivityContext
			/** The other options the form offered, so a picked alternative is not counted as her own words. */
			alternatives?: ContextOptions
			/** The typed kind of the landing page she came from, while her line still stands for it: the api does not cap it. */
			wants?: 'gap_fill'
		}) =>
			trackWait(
				'draft',
				(async () => {
					const res = await api.artifacts[':id'].activities.$post(
						{ param: { id: artifactId }, json: input },
						{ headers: key.headers() },
					)
					// `warning` is how the api says "the activity exists but drafting did not work".
					// Without surfacing it she lands on an empty editor with no explanation.
					return json<{ activity: Activity; exercises: Exercise[]; warning: string | null }>(res)
				})(),
				{ artifactId },
			),
		onSuccess: () => {
			key.done()
			qc.invalidateQueries({ queryKey: qk.activities })
			qc.invalidateQueries({ queryKey: qk.artifact(artifactId) })
			qc.invalidateQueries({ queryKey: qk.folders })
		},
	})
}

/** Her edits to the activity itself: its name, the folder she files it in, whether learners see the key, what they see of the source. */
export const usePatchActivity = (id: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (patch: {
			title?: string
			folderId?: string | null
			showAnswers?: boolean
			sourceView?: SourceViewMode
		}) => {
			const res = await api.activities[':id'].$patch({ param: { id }, json: patch })
			return (await json<{ activity: Activity }>(res)).activity
		},
		onSuccess: (activity, patch) => {
			qc.setQueryData(qk.activity(id), (prev: ActivityDetail | undefined) =>
				prev ? { ...prev, activity } : prev,
			)
			qc.invalidateQueries({ queryKey: qk.activities })
			if ('folderId' in patch) qc.invalidateQueries({ queryKey: qk.folders })
		},
	})
}

export const usePublishActivity = (id: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async () => {
			const res = await api.activities[':id'].publish.$post({ param: { id } })
			return (await json<{ activity: Activity }>(res)).activity
		},
		onSuccess: (activity) => {
			qc.setQueryData(qk.activity(id), (prev: ActivityDetail | undefined) =>
				prev ? { ...prev, activity } : prev,
			)
			qc.invalidateQueries({ queryKey: qk.activities })
		},
	})
}

/**
 * `assignment` narrows everything on the page to one group: an assignment id, `none` for the
 * general link, absent for all of it. The api filters; `assignments` is her groups for this quiz.
 */
export const useResults = (id: string | undefined, assignment?: string) =>
	useQuery({
		queryKey: qk.resultsFor(id ?? '', assignment),
		enabled: !!id,
		// She keeps this open while the class works; answers must arrive without a reload.
		refetchInterval: 15_000,
		refetchOnWindowFocus: true,
		// Switching group keeps the page (and its filter) on screen until the new numbers land; another
		// activity's numbers are never shown in its place.
		placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === id ? prev : undefined),
		queryFn: async () =>
			json<{
				attempts: Attempt[]
				/** Tries she took out of her results: in no count, listed apart so she can show them again. Absent from an api that predates hiding (web and api deploy apart). */
				hidden?: Attempt[]
				exercises: Exercise[]
				missed: Missed[]
				/** Absent from an api that predates the hint (web and api deploy apart). */
				keyHints?: KeyHint[]
				assignments: { id: string; group: string }[]
			}>(
				await api.activities[':id'].results.$get({
					param: { id: id as string },
					query: assignment ? { assignment } : {},
				}),
			),
	})

/** Per question, most missed first; derived on the api from the responses, never stored. */
export type Missed = {
	exerciseId: string
	wrong: number
	blank: number
	graded: number
	topWrong: { text: string; count: number } | null
	/** Matching and ordering: how many got each pair or place right. Absent from an api that predates it. */
	parts?: Parts | null
}

/**
 * A question where a clear majority of at least five chose the same answer the key calls wrong:
 * maybe the key is. `answer` is one of those answers, in the shape a learner's answer takes.
 */
export type KeyHint = {
	exerciseId: string
	chose: number
	of: number
	answer: AnyAnswer
}

/** A new draft on the same source aimed at what this class missed. */
export const useFollowUp = (id: string) => {
	const qc = useQueryClient()
	const key = useDraftKey()
	return useMutation({
		// `folderId` absent keeps the original's folder; null or an id sends it elsewhere.
		// `assignment` builds it from one group's misses, as the results filter shows them.
		mutationFn: (input: { folderId?: string | null; assignment?: string } = {}) =>
			trackWait(
				'follow_up',
				(async () =>
					json<{ activity: Activity; exercises: Exercise[] }>(
						await api.activities[':id']['follow-up'].$post(
							{ param: { id }, json: input },
							{ headers: key.headers() },
						),
					))(),
				{ activityId: id },
			),
		onSuccess: () => {
			key.done()
			qc.invalidateQueries({ queryKey: qk.activities })
			qc.invalidateQueries({ queryKey: qk.folders })
		},
	})
}

/** Every number counted from this activity's attempts: its results, the hub, its group counts. */
const refreshCounts = (qc: QueryClient, activityId: string) =>
	Promise.all([
		qc.invalidateQueries({ queryKey: qk.results(activityId) }),
		qc.invalidateQueries({ queryKey: qk.resultsHub }),
		qc.invalidateQueries({ queryKey: qk.assignments(activityId) }),
	])

/** Her verdict on one handed-in answer, over whatever marked it; the results and "most missed" follow on the refetch. */
export const useMarkAnswer = (activityId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (v: { attemptId: string; exerciseId: string; correct: boolean }) =>
			json<{ ok?: true }>(
				await api.activities[':id'].attempts[':attemptId'].responses[':exerciseId'].verdict.$put({
					param: { id: activityId, attemptId: v.attemptId, exerciseId: v.exerciseId },
					json: { correct: v.correct },
				}),
			),
		onSuccess: () => refreshCounts(qc, activityId),
	})
}

/** What a re-check came to: answers the AI ruled on, how many of those changed, how many it could not check now. */
export type Rechecked = { checked: number; changed: number; unavailable: number }

/**
 * "Re-check with AI": the model looks again at the written answers that are wrong or waiting, on
 * this page's group when one is picked. Her own marks are never touched; the numbers follow on the refetch.
 */
export const useRecheck = (activityId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (assignment?: string) =>
			json<Rechecked>(
				await api.activities[':id'].recheck.$post({
					param: { id: activityId },
					json: assignment ? { assignment } : {},
				}),
			),
		// Also when refused: a stale tab then shows the rows as they are now.
		onSettled: () => refreshCounts(qc, activityId),
	})
}

/** Lets a handed-in name start fresh once; the row's offer turns into "allowed" on the refetch. */
export const useAllowRetry = (activityId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (attemptId: string) =>
			json<{ ok: true }>(
				await api.activities[':id'].attempts[':attemptId']['allow-retry'].$post({
					param: { id: activityId, attemptId },
				}),
			),
		// Also when refused: a stale tab then shows the row as it is now.
		onSettled: () => refreshCounts(qc, activityId),
	})
}

/** Takes one try out of her results, or puts it back; every number follows on the refetch. */
export const useHideAttempt = (activityId: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (v: { attemptId: string; hidden: boolean }) =>
			json<{ ok: true; hidden: boolean }>(
				await api.activities[':id'].attempts[':attemptId'].hidden.$put({
					param: { id: activityId, attemptId: v.attemptId },
					json: { hidden: v.hidden },
				}),
			),
		// Also when refused: a stale tab then shows the row as it is now.
		onSettled: () => refreshCounts(qc, activityId),
	})
}
